import { findArticle, getArticleBody, getArticleSummary, getArticleTitle } from "./articles";
import type { HelpArticle } from "./types";
import { helpArticlePath } from "../../../../../lib/copy/src/help-article-path.mjs";

export type HelpArticleView = Pick<HelpArticle,
  "slug" | "topic" | "title" | "summary" | "body" | "updatedAt" | "publishedAt"> & { related: string[] };
export interface HelpArticleTemplate {
  id: "help-article";
  pathname: string;
  language: string;
  sourceReference: string;
  data: HelpArticleView;
}

/** Actual authored records and their existing locale helpers; no HTML snapshot. */
export function buildHelpArticleView(slug: string, language: string): HelpArticleView | null {
  const article = findArticle(slug);
  if (!article) return null;
  return {
    slug: article.slug, topic: article.topic,
    title: getArticleTitle(article, language), summary: getArticleSummary(article, language),
    body: getArticleBody(article, language),
    updatedAt: article.updatedAt, publishedAt: article.publishedAt,
    related: article.related || [],
  };
}
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const baseLanguage = (language: string) => language.split(/[-_]/)[0].toLowerCase();

/** A different route/locale is CSR, not permission to reuse the old article.
 * A matching but malformed required payload fails rather than silently re-reading. */
export function seededHelpArticleView(seed: unknown, slug: string, language: string): HelpArticleView | null {
  if (!object(seed) || seed.kind !== "compiled" || !object(seed.publicTemplate)) return null;
  const template = seed.publicTemplate;
  if (template.id !== "help-article" || template.pathname !== helpArticlePath(slug) ||
      typeof template.language !== "string" ||
      baseLanguage(template.language) !== baseLanguage(language)) return null;
  const data = template.data;
  if (template.sourceReference !== `help-article:${slug}` || !object(data) ||
      data.slug !== slug || typeof data.topic !== "string" ||
      typeof data.title !== "string" || typeof data.summary !== "string" ||
      typeof data.updatedAt !== "string" || !Number.isFinite(Date.parse(data.updatedAt)) ||
      data.publishedAt !== undefined && (typeof data.publishedAt !== "string" ||
        !Number.isFinite(Date.parse(data.publishedAt))) ||
      !Array.isArray(data.body) || !data.body.every(object) ||
      !Array.isArray(data.related) || !data.related.every(value => typeof value === "string"))
    throw Error("Invalid required public Help article view");
  return data as HelpArticleView;
}
